import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ListTodo, Settings, Images, FileText, BookOpen, Microscope, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import MobileProjectTabDropdown from './MobileProjectTabDropdown';
import { useUserProfile } from '@/hooks/profile/useUserProfile';
import { getTabSettings } from '@/hooks/project/use-project-settings';

interface ProjectTabNavigationProps {
  projectId: string;
  goalId?: string;
  agentThreadId?: string;
  project?: any;
  goal?: any;
}

/**
 * ProjectTabNavigation - Route-based navigation for project pages
 * Matches the original WorkbookTabNavigation design exactly
 */
const ProjectTabNavigation: React.FC<ProjectTabNavigationProps> = ({
  projectId,
  goalId,
  agentThreadId,
  project,
  goal
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useIsMobile();
  const { profile } = useUserProfile();
  
  // Get tab settings from project
  const tabSettings = useMemo(() => {
    return getTabSettings(project?.settings);
  }, [project?.settings]);
  
  // Determine active tab from current route
  const getActiveTab = () => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    
    if (location.pathname.includes('/page/')) return 'page';
    if (lastSegment === 'settings') return 'settings';
    if (lastSegment === 'services') return 'work_order';
    if (lastSegment === 'images') return 'images';
    if (lastSegment === 'resources') return 'resources';
    if (lastSegment === 'pages') return 'pages';
    return 'tasks'; // default
  };
  
  const activeTab = getActiveTab();
  
  // Navigation handlers
  const handleTasksClick = () => navigate(`/project/${projectId}/tasks`);
  const handleSettingsClick = () => navigate(`/project/${projectId}/settings`);
  const handleImagesClick = () => navigate(`/project/${projectId}/images`);
  const handleResourcesClick = () => navigate(`/project/${projectId}/resources`);
  const handlePagesClick = () => navigate(`/project/${projectId}/pages`);
  const handleServicesClick = () => navigate(`/project/${projectId}/services`);
  
  // Render mobile dropdown on mobile, desktop tabs on larger screens
  if (isMobile) {
    return (
      <MobileProjectTabDropdown
        projectId={projectId}
        goalId={goalId}
        agentThreadId={agentThreadId}
        project={project}
        goal={goal}
      />
    );
  }

  return (
    <div className="flex items-center gap-2 p-2 bg-white/50 dark:bg-gray-700/30 backdrop-blur-sm border border-purple-100/40 dark:border-purple-800/30 rounded-xl shadow-lg shadow-purple-500/10 overflow-hidden">
      
      {/* Tasks Button - FIRST POSITION */}
      {tabSettings.tasks && (
        <motion.button
        onClick={handleTasksClick}
        className={cn(
          "flex-1 md:flex-shrink-0 flex items-center justify-center px-4 py-2 rounded-lg",
          "border border-transparent backdrop-blur-sm",
          activeTab === 'tasks'
            ? "bg-gradient-to-br from-purple-500/90 to-blue-500/90 text-white border-purple-300/50 dark:border-purple-600/50"
            : "bg-white/40 dark:bg-white/10 text-gray-700 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-white/15 hover:shadow-md border-purple-200/20 dark:border-purple-700/20"
        )}
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
          <ListTodo className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline text-sm font-medium whitespace-nowrap">Tasks</span>
        </motion.button>
      )}

      {/* Pages Button - SECOND POSITION */}
      {tabSettings.documents && (
        <motion.button
        onClick={handlePagesClick}
        className={cn(
          "flex-1 md:flex-shrink-0 flex items-center justify-center px-4 py-2 rounded-lg",
          "border border-transparent backdrop-blur-sm",
          activeTab === 'pages'
            ? "bg-gradient-to-br from-purple-500/90 to-blue-500/90 text-white border-purple-300/50 dark:border-purple-600/50"
            : "bg-white/40 dark:bg-white/10 text-gray-700 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-white/15 hover:shadow-md border-purple-200/20 dark:border-purple-700/20"
        )}
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
          <FileText className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline text-sm font-medium whitespace-nowrap">Documents</span>
        </motion.button>
      )}

      {/* Research Button - THIRD POSITION */}
      {tabSettings.research && (
        <motion.button
        onClick={handleResourcesClick}
        className={cn(
          "flex-1 md:flex-shrink-0 flex items-center justify-center px-4 py-2 rounded-lg",
          "border border-transparent backdrop-blur-sm",
          activeTab === 'resources'
            ? "bg-gradient-to-br from-purple-500/90 to-blue-500/90 text-white border-purple-300/50 dark:border-purple-600/50"
            : "bg-white/40 dark:bg-white/10 text-gray-700 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-white/15 hover:shadow-md border-purple-200/20 dark:border-purple-700/20"
        )}
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
          <Microscope className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline text-sm font-medium whitespace-nowrap">Research</span>
        </motion.button>
      )}

      {/* Images Button - FOURTH POSITION */}
      {tabSettings.images && (
        <motion.button
        onClick={handleImagesClick}
        className={cn(
          "flex-1 md:flex-shrink-0 flex items-center justify-center px-4 py-2 rounded-lg",
          "border border-transparent backdrop-blur-sm",
          activeTab === 'images'
            ? "bg-gradient-to-br from-purple-500/90 to-blue-500/90 text-white border-purple-300/50 dark:border-purple-600/50"
            : "bg-white/40 dark:bg-white/10 text-gray-700 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-white/15 hover:shadow-md border-purple-200/20 dark:border-purple-700/20"
        )}
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
          <Images className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline text-sm font-medium whitespace-nowrap">Images</span>
        </motion.button>
      )}

      {/* Work Order Button - FIFTH POSITION */}
      {tabSettings.work_order && (
        <motion.button
          onClick={handleServicesClick}
          className={cn(
            "flex-1 md:flex-shrink-0 flex items-center justify-center px-4 py-2 rounded-lg",
            "border border-transparent backdrop-blur-sm",
            activeTab === 'work_order'
              ? "bg-gradient-to-br from-purple-500/90 to-blue-500/90 text-white border-purple-300/50 dark:border-purple-600/50"
              : "bg-white/40 dark:bg-white/10 text-gray-700 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-white/15 hover:shadow-md border-purple-200/20 dark:border-purple-700/20"
          )}
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        >
          <ClipboardList className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline text-sm font-medium whitespace-nowrap">Work Order</span>
        </motion.button>
      )}

      {/* Settings Button - SIXTH POSITION */}
      <motion.button
        onClick={handleSettingsClick}
        className={cn(
          "flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg backdrop-blur-sm",
          activeTab === 'settings'
            ? "bg-gradient-to-br from-purple-500/90 to-blue-500/90 text-white border border-purple-300/50 dark:border-purple-600/50"
            : "bg-white/40 dark:bg-white/10 text-gray-700 dark:text-gray-200 hover:bg-white/70 dark:hover:bg-white/15 hover:shadow-md border border-purple-200/20 dark:border-purple-700/20"
        )}
        whileHover={{ scale: 1.05, y: -1 }}
        whileTap={{ scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        aria-label="Project Settings"
        title="Project Settings"
      >
        <Settings className="h-4 w-4" />
      </motion.button>


    </div>
  );
};

export default ProjectTabNavigation; 